/**
 * 1RM Calculator using Brzycki and Epley formulas
 */
import { roundToNearestPlate } from './plateMath';

// Brzycki formula: weight × (36 / (37 - reps))
export function calculate1RMBrzycki(weight: number, reps: number): number {
  if (reps === 1) return roundToNearestPlate(weight);
  if (reps > 10) return calculate1RMEpley(weight, reps);
  return roundToNearestPlate(weight * (36 / (37 - reps)));
}

// Epley formula: weight × (1 + reps / 30)
export function calculate1RMEpley(weight: number, reps: number): number {
  if (reps === 1) return roundToNearestPlate(weight);
  return roundToNearestPlate(weight * (1 + reps / 30));
}

// Calculate target load based on %1RM (rounded to nearest 5 lbs)
export function calculateTargetLoad(oneRM: number, percentage: number): number {
  return Math.round((oneRM * percentage) / 5) * 5;
}

// RPE to %1RM conversion (single-rep approximation — kept for callers that
// only have an RPE, no rep context). For load prescription use
// rpeToWorkingWeight(), which uses the full reps×RPE chart below.
export const RPE_TO_PERCENT: Record<number, number> = {
  10: 1.0,    // Max effort
  9.5: 0.978,
  9: 0.955,
  8.5: 0.939,
  8: 0.922,
  7.5: 0.907,
  7: 0.892,
  6.5: 0.878,
  6: 0.863,
};

// ============================================================================
// RPE / RIR LOAD CHART (Helms / RTS) — reps × RPE → %1RM
// ============================================================================
// The previous model (a flat per-RPE % times a coarse rep multiplier) ran
// systematically hot — up to ~8% over published values at higher reps —
// because it double-discounts. Strength science maps load to BOTH proximity to
// failure (RPE/RIR) AND rep count jointly; this is that 2D chart (fractions of
// 1RM). Columns are RPE 6–10 in 0.5 steps; rows are reps 1–12. Off-grid RPE is
// linearly interpolated; reps are clamped to [1,12].
const RPE_LOAD_CHART: Record<number, Record<number, number>> = {
  1:  {10:1.000, 9.5:0.978, 9:0.955, 8.5:0.939, 8:0.922, 7.5:0.907, 7:0.892, 6.5:0.878, 6:0.863},
  2:  {10:0.955, 9.5:0.939, 9:0.922, 8.5:0.907, 8:0.892, 7.5:0.878, 7:0.863, 6.5:0.850, 6:0.837},
  3:  {10:0.922, 9.5:0.907, 9:0.892, 8.5:0.878, 8:0.863, 7.5:0.850, 7:0.837, 6.5:0.824, 6:0.811},
  4:  {10:0.892, 9.5:0.878, 9:0.863, 8.5:0.850, 8:0.837, 7.5:0.824, 7:0.811, 6.5:0.799, 6:0.786},
  5:  {10:0.863, 9.5:0.850, 9:0.837, 8.5:0.824, 8:0.811, 7.5:0.799, 7:0.786, 6.5:0.774, 6:0.762},
  6:  {10:0.837, 9.5:0.824, 9:0.811, 8.5:0.799, 8:0.786, 7.5:0.774, 7:0.762, 6.5:0.751, 6:0.739},
  7:  {10:0.811, 9.5:0.799, 9:0.786, 8.5:0.774, 8:0.762, 7.5:0.751, 7:0.739, 6.5:0.723, 6:0.707},
  8:  {10:0.786, 9.5:0.774, 9:0.762, 8.5:0.751, 8:0.739, 7.5:0.723, 7:0.707, 6.5:0.694, 6:0.680},
  9:  {10:0.762, 9.5:0.751, 9:0.739, 8.5:0.723, 8:0.707, 7.5:0.694, 7:0.680, 6.5:0.667, 6:0.653},
  10: {10:0.739, 9.5:0.723, 9:0.707, 8.5:0.694, 8:0.680, 7.5:0.667, 7:0.653, 6.5:0.640, 6:0.626},
  11: {10:0.707, 9.5:0.694, 9:0.680, 8.5:0.667, 8:0.653, 7.5:0.640, 7:0.626, 6.5:0.613, 6:0.599},
  12: {10:0.680, 9.5:0.667, 9:0.653, 8.5:0.640, 8:0.626, 7.5:0.613, 7:0.599, 6.5:0.586, 6:0.572},
};

// %1RM for a given reps × RPE, interpolating RPE off the 0.5 grid.
export function percentForRepsRPE(reps: number, rpe: number): number {
  const r = Math.max(1, Math.min(12, Math.round(reps)));
  const rpeC = Math.max(6, Math.min(10, rpe));
  const row = RPE_LOAD_CHART[r];
  const lo = Math.floor(rpeC * 2) / 2;
  const hi = Math.ceil(rpeC * 2) / 2;
  if (lo === hi) return row[lo];
  const t = (rpeC - lo) / (hi - lo);
  return row[lo] + (row[hi] - row[lo]) * t;
}

// Convert a target RPE + rep count to a working weight, using the Helms/RTS
// reps×RPE load chart (science-calibrated; within ~1% of published values).
export function rpeToWorkingWeight(
  oneRM: number,
  reps: number,
  rpe: number
): number {
  return calculateTargetLoad(oneRM, percentForRepsRPE(reps, rpe));
}

// Suggest 1RM update based on logged performance
export function shouldUpdate1RM(
  currentRM: number,
  loggedWeight: number,
  loggedReps: number,
  loggedRPE: number
): { shouldUpdate: boolean; suggested1RM: number } {
  const estimated1RM = calculate1RMBrzycki(loggedWeight, loggedReps);
  const rpeAdjustment = loggedRPE <= 8 ? 1.05 : 1.0;
  const adjusted1RM = estimated1RM * rpeAdjustment;
  
  return {
    shouldUpdate: adjusted1RM > currentRM * 1.025,
    suggested1RM: roundToNearestPlate(adjusted1RM),
  };
}

/**
 * Bodyweight-plus-load lifts (weighted pull-ups / chin-ups / dips / muscle-ups).
 *
 * %1RM ARITHMETIC IS INVALID ON THESE (audit P0-7). Percent-of-1RM math assumes
 * the stored max and the prescribed load live on the same ratio scale with a
 * true zero at "no load". For these lifts the muscles move BODYWEIGHT + added
 * load, but the app stores/prescribes only the ADDED load — a scale whose zero
 * is actually "one bodyweight of resistance". Taking a % of the added-load max
 * therefore OVER-prescribes, badly: at BW 200 with an added-load 1RM of 100,
 * "84%" computed on added load is 84 lb added = (200+84)/(200+100) = 94.7% of
 * the true system max — a near-max triple prescribed as a rep-work set.
 * (The stored pullUps value may not even be a load — for unweighted users it
 * is a REP COUNT, which makes % math doubly meaningless.)
 *
 * The correct computation would be pct × (BW + added1RM) − BW, but bodyweight
 * is not reliably available at the %-derivation call sites and the stored
 * key's semantics (rep count vs added load) are ambiguous in legacy data — so
 * these lifts are EXCLUDED from % derivation instead. Downstream engines fall
 * back to last-used added load / statistical history, which compare added load
 * to added load (a shift, not a ratio) and are direction-safe. This matches
 * e1rmAnchor.ts, which already excludes them from e1RM anchoring for the same
 * reason.
 */
export function isLoadPlusBodyweightLift(exerciseName: string): boolean {
  const n = (exerciseName || '').toLowerCase();
  return /pull[\s-]?up|chin[\s-]?up|muscle[\s-]?up|\bdips?\b/.test(n);
}

// Calculate plate loading for a given weight
export function calculatePlateLoading(
  targetWeight: number,
  barWeight: number = 45
): number[] {
  const weightPerSide = (targetWeight - barWeight) / 2;
  const plates = [45, 35, 25, 10, 5, 2.5];
  const result: number[] = [];
  let remaining = weightPerSide;

  for (const plate of plates) {
    while (remaining >= plate) {
      result.push(plate);
      remaining -= plate;
    }
  }

  return result;
}

/**
 * CANONICAL 1RM KEY MAPPING
 * 
 * This maps various exercise names/aliases to a single canonical key.
 * Prevents duplicate 1RM entries for the same exercise.
 * 
 * Format: canonicalKey → [list of aliases that should map to it]
 */
export const CANONICAL_1RM_KEYS: Record<string, string[]> = {
  // DEADLIFTS - all map to 'deadlift'
  'deadlift': [
    'deadlift', 'conventional deadlift', 'barbell deadlift', 'dl',
    'conventional dl', 'dead lift', 'deadlifts'
  ],
  'sumoDeadlift': [
    'sumo deadlift', 'sumo dl', 'sumo'
  ],
  'romanianDeadlift': [
    'romanian deadlift', 'rdl', 'stiff leg deadlift', 'stiff legged deadlift',
    'romanian dl', 'stiff leg dl'
  ],
  'trapBarDeadlift': [
    'trap bar deadlift', 'hex bar deadlift', 'trap bar dl', 'hex bar dl'
  ],
  'deficitDeadlift': [
    'deficit deadlift', 'deficit dl'
  ],
  
  // SQUATS - all map to 'backSquat'
  'backSquat': [
    'back squat', 'squat', 'barbell squat', 'low bar squat', 'high bar squat',
    'back squat (low bar)', 'back squat (high bar)', 'squat (back)'
  ],
  'frontSquat': [
    'front squat', 'squat (front)', 'front squat (clean grip)', 'front squat (cross grip)'
  ],
  
  // BENCH - all map to 'benchPress'
  'benchPress': [
    'bench press', 'bench', 'barbell bench press', 'flat bench press',
    'barbell bench', 'flat bench', 'bb bench'
  ],
  'inclineBenchPress': [
    'incline bench press', 'incline bench', 'incline bb bench', 'incline barbell bench'
  ],
  'inclineDB': [
    'incline db press', 'incline dumbbell press', 'incline dumbbell bench press',
    'incline db bench', 'incline dumbbell bench'
  ],
  'dumbbellBenchPress': [
    'dumbbell bench press', 'db bench press', 'db bench', 'dumbbell bench', 'flat db bench'
  ],
  
  // OVERHEAD PRESS
  'overheadPress': [
    'overhead press', 'ohp', 'shoulder press', 'military press',
    'standing press', 'press', 'strict press', 'barbell press'
  ],
  'dumbbellShoulderPress': [
    'dumbbell shoulder press', 'db shoulder press', 'seated dumbbell press',
    'standing dumbbell press', 'db ohp', 'dumbbell overhead press'
  ],
  
  // ROWS
  'barbellRow': [
    'barbell row', 'bent over row', 'bent over barbell row', 'pendlay row',
    'bb row', 'row', 'bent row', 'barbell row (pendlay)'
  ],
  'dumbbellRow': [
    'dumbbell row', 'db row', 'one arm dumbbell row', 'single arm row',
    'one arm row'
  ],
  
  // PULL-UPS / CHIN-UPS
  'pullUps': [
    'pull-ups', 'pull ups', 'pullups', 'pullup', 'pull-up',
    'weighted pull-ups', 'weighted pullups', 'pull-ups (weighted)'
  ],
  'chinUps': [
    'chin-ups', 'chin ups', 'chinups', 'chinup', 'chin-up',
    'weighted chin-ups', 'weighted chinups'
  ],
  
  // ACCESSORY LIFTS
  'preacherCurl': [
    'preacher curl', 'preacher curls', 'ez bar preacher curl', 'db preacher curl'
  ],
  'dumbbellCurl': [
    'dumbbell curl', 'db curl', 'bicep curl', 'curls', 'hammer curl',
    'db bicep curl', 'dumbbell curls', 'bicep curls'
  ],
  'cableCurl': [
    'cable curl', 'cable curls', 'cable bicep curl'
  ],
  'tricepPushdown': [
    'tricep pushdown', 'tricep pulldown', 'cable pushdown', 'rope pushdown',
    'triceps pushdown', 'tricep push down', 'pushdown'
  ],
  'skullCrushers': [
    'skull crusher', 'skull crushers', 'lying tricep extension', 'ez bar skull crusher'
  ],
  'lateralRaise': [
    'lateral raise', 'side raise', 'dumbbell lateral raise', 'side lateral raise',
    'lateral raises', 'side raises'
  ],
  'rearDeltFly': [
    'rear delt fly', 'reverse fly', 'rear delt raise', 'reverse pec deck',
    'rear delt flyes', 'bent over fly'
  ],
  'shrug': [
    'shrug', 'shrugs', 'barbell shrug', 'dumbbell shrug', 'db shrug', 'bb shrug',
    'trap shrug'
  ],
  'calfRaise': [
    'calf raise', 'calf raises', 'standing calf raise', 'seated calf raise',
    'calf press', 'calf press on the leg press machine'
  ],
  
  // CARRIES
  'farmerCarry': [
    'farmer carry', "farmer's carry", 'farmers carry', 'farmer walk',
    "farmer's walk", 'farmers walk', 'farmer carries'
  ],
  'suitcaseCarry': [
    'suitcase carry', 'suitcase carries', 'single arm carry', 'suitcase walk'
  ],
  
  // OLYMPIC LIFTS
  'powerClean': [
    'power clean', 'clean', 'hang clean', 'hang power clean'
  ],
  'powerSnatch': [
    'power snatch', 'snatch', 'hang snatch', 'hang power snatch'
  ],
  
  // HIP HINGE / POSTERIOR CHAIN
  'hipThrust': [
    'hip thrust', 'barbell hip thrust', 'glute bridge', 'bb hip thrust'
  ],
  'backExtension': [
    'back extension', 'hyperextension', 'roman chair', 'back extensions'
  ],
  'goodMorning': [
    'good morning', 'good mornings', 'barbell good morning'
  ],
  
  // LEGS
  'legPress': [
    'leg press', 'leg press machine', '45 degree leg press'
  ],
  'legCurl': [
    'leg curl', 'lying leg curl', 'seated leg curl', 'hamstring curl'
  ],
  'legExtension': [
    'leg extension', 'leg extensions', 'quad extension'
  ],
  'bulgarianSplitSquat': [
    'bulgarian split squat', 'rear foot elevated split squat', 'split squat',
    'bss', 'rfess'
  ],
};

/**
 * Get the canonical 1RM key for an exercise name
 * Returns the canonical key (camelCase) that should be used for storage
 */
export function getCanonical1RMKey(exerciseName: string): string {
  const normalized = exerciseName.toLowerCase().trim();
  
  // Check each canonical key's aliases - EXACT MATCH ONLY
  for (const [canonicalKey, aliases] of Object.entries(CANONICAL_1RM_KEYS)) {
    if (aliases.some(alias => alias.toLowerCase() === normalized)) {
      return canonicalKey;
    }
  }
  
  // Not found in mapping - generate a camelCase key
  // "Bicep Curl" -> "bicepCurl"
  // "Dumbbell Bench Press" -> "dumbbellBenchPress"
  return exerciseName
    .split(/\s+/)
    .map((word, index) => {
      const lower = word.toLowerCase().replace(/[^a-z0-9]/g, '');
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * Find the best 1RM value for an exercise from the oneRMs object
 * Handles different key formats and aliases
 */
export function find1RMValue(
  exerciseName: string, 
  oneRMs: Record<string, number>
): { key: string; value: number } | null {
  // First, try the canonical key
  const canonicalKey = getCanonical1RMKey(exerciseName);
  if (oneRMs[canonicalKey] && oneRMs[canonicalKey] > 0) {
    return { key: canonicalKey, value: oneRMs[canonicalKey] };
  }
  
  // Try lowercase version
  const lowerKey = canonicalKey.toLowerCase();
  if (oneRMs[lowerKey] && oneRMs[lowerKey] > 0) {
    return { key: lowerKey, value: oneRMs[lowerKey] };
  }
  
  // Try finding any key that matches our aliases
  const aliases = CANONICAL_1RM_KEYS[canonicalKey] || [];
  for (const alias of aliases) {
    // Convert alias to possible key formats
    const aliasKey = alias.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
    const aliasKeyLower = aliasKey.toLowerCase();
    const aliasKeyCamel = alias
      .split(/\s+/)
      .map((word, index) => {
        const lower = word.toLowerCase().replace(/[^a-z0-9]/g, '');
        return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join('');
    
    // Check all variations
    for (const tryKey of [aliasKey, aliasKeyLower, aliasKeyCamel]) {
      if (oneRMs[tryKey] && oneRMs[tryKey] > 0) {
        return { key: tryKey, value: oneRMs[tryKey] };
      }
    }
  }
  
  // Last resort: fuzzy match on existing keys
  const normalized = exerciseName.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const [key, value] of Object.entries(oneRMs)) {
    const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value > 0 && (keyNorm.includes(normalized) || normalized.includes(keyNorm))) {
      return { key, value };
    }
  }
  
  return null;
}

/**
 * Consolidate duplicate case/format-variant 1RM keys into one canonical key each.
 *
 * Real user data accumulated keys like `backSquat: 270` AND `backsquat: 325`
 * for the same lift (different writers used different casing). This collapses
 * every group of keys that normalize to the same lift into a SINGLE canonical
 * key, keeping the MAX value — duplicates are the same lift logged at different
 * times, and the highest is the conservative-correct baseline (matches the
 * behavior of lookup1RM in smartWorkoutGenerator).
 *
 * Canonical key selection, in priority order:
 *  1. A top-level key in CANONICAL_1RM_KEYS that normalizes to the group
 *     (e.g. 'backSquat', 'benchPress') — this is exactly what find1RMValue
 *     resolves human exercise names to, so reads stay consistent afterward.
 *  2. The camelCase member of the group (the one containing an uppercase letter).
 *  3. The first key encountered.
 *
 * Only keys whose NORMALIZED form is identical are merged, so distinct metrics
 * like `benchPress` and `benchPressBwMaxReps` are never collapsed together.
 */
export function consolidateOneRMKeys(
  oneRMs: Record<string, number>
): {
  cleaned: Record<string, number>;
  collapsed: Array<{ canonical: string; from: string[]; value: number }>;
} {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Pre-index the canonical map keys by their normalized form.
  const canonicalByNorm = new Map<string, string>();
  for (const canonicalKey of Object.keys(CANONICAL_1RM_KEYS)) {
    canonicalByNorm.set(norm(canonicalKey), canonicalKey);
  }

  // Group existing keys by normalized form.
  const groups = new Map<string, string[]>();
  for (const key of Object.keys(oneRMs)) {
    const n = norm(key);
    if (!groups.has(n)) groups.set(n, []);
    groups.get(n)!.push(key);
  }

  const cleaned: Record<string, number> = {};
  const collapsed: Array<{ canonical: string; from: string[]; value: number }> = [];

  groups.forEach((keys, n) => {
    if (keys.length === 1) {
      cleaned[keys[0]] = oneRMs[keys[0]];
      return;
    }
    const maxVal = Math.max(...keys.map(k => oneRMs[k] || 0));
    const canonical =
      canonicalByNorm.get(n) ||
      keys.find(k => /[A-Z]/.test(k)) ||
      keys[0];
    cleaned[canonical] = maxVal;
    collapsed.push({ canonical, from: keys.slice(), value: maxVal });
  });

  return { cleaned, collapsed };
}

/**
 * Identify duplicate 1RM keys that should be consolidated
 * Returns a map of canonicalKey -> [duplicateKeys]
 */
export function findDuplicate1RMKeys(
  oneRMs: Record<string, number>
): Map<string, { duplicates: string[]; values: number[] }> {
  const keysByCanonical = new Map<string, { duplicates: string[]; values: number[] }>();
  
  for (const key of Object.keys(oneRMs)) {
    const canonicalKey = getCanonical1RMKey(key);
    
    if (!keysByCanonical.has(canonicalKey)) {
      keysByCanonical.set(canonicalKey, { duplicates: [], values: [] });
    }
    
    const entry = keysByCanonical.get(canonicalKey)!;
    entry.duplicates.push(key);
    entry.values.push(oneRMs[key]);
  }
  
  // Filter to only entries with duplicates
  const duplicates = new Map<string, { duplicates: string[]; values: number[] }>();
  keysByCanonical.forEach((value, key) => {
    if (value.duplicates.length > 1) {
      duplicates.set(key, value);
    }
  });
  
  return duplicates;
}

